import { FluentIcon } from "@fluentui/react-icons";

export interface SearchItem {
    [key: string]: unknown;
    id: string;
    title: string;
    description?: string;
    category: string;
    originalCategory?: string;
    onClick?: () => void;
    appPath?: string;
    icon?: FluentIcon;
}

export interface ScoredSearchItem extends SearchItem {
    score: number;
}

export interface CategoryWithScores {
    category: string;
    items: ScoredSearchItem[];
    maxScore: number;
}

const TITLE_MATCH_POINTS = 200;
const TITLE_PREFIX_POINTS = 100;
const TITLE_INCLUDES_POINTS = 20;
const TITLE_WORD_PREFIX_POINTS = 50;
const DESCRIPTION_INCLUDES_POINTS = 10;

/**
 * Calculates relevance score for a search item based on keyword matching
 * @param item - The item to score (must have title and optional description)
 * @param keyword - The search keyword (should be lowercase)
 * @returns The calculated score
 */
export function calculateRelevanceScore(item: { title: string; description?: string }, keyword: string): number {
    let score = 0;
    const title = item.title.toLowerCase() || "";
    const description = item.description?.toLowerCase() || "";

    if (keyword) {
        if (title === keyword) {
            score += TITLE_MATCH_POINTS;
        } else if (title.startsWith(keyword)) {
            score += TITLE_PREFIX_POINTS;
        } else if (title.includes(keyword)) {
            score += TITLE_INCLUDES_POINTS;
        }

        const titleWords = title.split(" ");
        if (titleWords.some((word) => word.startsWith(keyword))) {
            score += TITLE_WORD_PREFIX_POINTS;
        }

        if (description.includes(keyword)) {
            score += DESCRIPTION_INCLUDES_POINTS;
        }
    }

    return score;
}

/**
 * Scores and filters search results based on keyword relevance
 * @param items - Array of items to score
 * @param keyword - The search keyword (should be lowercase)
 * @returns Array of scored items, filtered to only include items with score > 0
 */
export function scoreAndFilterResults(items: SearchItem[], keyword: string): ScoredSearchItem[] {
    return items
        .map((item) => ({
            ...item,
            score: calculateRelevanceScore(item, keyword)
        }))
        .filter((item) => !keyword || item.score > 0);
}

/**
 * Groups scored items by category and sorts within each category by score
 * @param scoredItems - Array of scored items with category property
 * @returns Map of category name to sorted array of items
 */
export function groupByCategory(scoredItems: ScoredSearchItem[]): Map<string, ScoredSearchItem[]> {
    const grouped = new Map<string, ScoredSearchItem[]>();

    for (const item of scoredItems) {
        const category = item.category;
        const arr = grouped.get(category) || [];
        arr.push(item);
        grouped.set(category, arr);
    }

    for (const [category, items] of grouped.entries()) {
        const uniqueItems: ScoredSearchItem[] = [];
        const seenIds = new Set<string>();

        for (const item of items) {
            if (!seenIds.has(item.id)) {
                seenIds.add(item.id);
                uniqueItems.push(item);
            }
        }

        grouped.set(category, uniqueItems);
    }

    for (const items of grouped.values()) {
        items.sort((a, b) => b.score - a.score);
    }

    return grouped;
}

/**
 * Creates categorized results with top results section
 * @param grouped - Map of category to items
 * @param maxResultsPerCategory - Maximum number of results per category
 * @param skipTopResults - If true, skip creating a Top Results category
 * @returns Array of categories with their items and max scores
 */
export function createCategorizedResults(
    grouped: Map<string, ScoredSearchItem[]>,
    currentCategories: Set<string>,
    maxResultsPerCategory: number = 5,
    skipTopResults: boolean = false,
    contextCategory?: string
): CategoryWithScores[] {
    const filteredGrouped = [...grouped.entries()].filter(([category]) => currentCategories.has(category));
    const filteredMap = new Map(filteredGrouped);

    const allItemsSorted = Array.from(filteredMap.values())
        .flat()
        .sort((a, b) => {
            const aIsContext = contextCategory && a.category.toLowerCase() === contextCategory.toLowerCase() ? 1 : 0;
            const bIsContext = contextCategory && b.category.toLowerCase() === contextCategory.toLowerCase() ? 1 : 0;
            if (aIsContext !== bIsContext) {
                return bIsContext - aIsContext;
            }
            return b.score - a.score;
        });

    const topResults = skipTopResults ? [] : allItemsSorted.slice(0, maxResultsPerCategory);
    const topResultIds = new Set(topResults.map((item) => item.id));

    let categoriesWithScores = Array.from(filteredMap.entries()).map(([category, items]) => {
        const filteredItems = skipTopResults ? items : items.filter((item) => !topResultIds.has(item.id));
        const maxScore = filteredItems.length > 0 ? Math.max(...filteredItems.map((item) => item.score)) : 0;
        return {
            category,
            items: filteredItems.slice(0, maxResultsPerCategory),
            maxScore
        };
    });

    categoriesWithScores = categoriesWithScores.filter(({ items }) => items.length > 0).sort((a, b) => b.maxScore - a.maxScore);

    if (!skipTopResults && topResults.length > 0) {
        categoriesWithScores.unshift({
            category: "Top Results",
            items: topResults,
            maxScore: topResults[0]?.score ?? 0
        });
    }

    return categoriesWithScores;
}

/**
 * Flattens categorized results for UI consumption, removing scores
 * @param categoriesWithScores - Array of categories with their items
 * @returns Flattened array of items with category information preserved
 */
export function flattenCategorizedResults(categoriesWithScores: CategoryWithScores[]): SearchItem[] {
    return categoriesWithScores.flatMap(({ category, items }) =>
        items.map(({ score, ...item }) => ({
            ...item,
            originalCategory: item.originalCategory || item.category, // Preserve existing originalCategory if it exists
            category // Set new category (like "Top Results")
        }))
    );
}

/**
 * Complete search result processing pipeline
 * @param items - Raw search items
 * @param keyword - Search keyword (should be lowercase)
 * @param maxResultsPerCategory - Maximum results per category
 * @param skipTopResults - If true, skip creating a Top Results category
 * @returns Processed and flattened results ready for UI
 */
export function processSearchResults(
    items: SearchItem[],
    keyword: string,
    currentCategories: Set<string>,
    maxResultsPerCategory: number = 5,
    skipTopResults: boolean = false,
    contextCategory?: string
): SearchItem[] {
    const scoredItems = scoreAndFilterResults(items, keyword);

    const grouped = groupByCategory(scoredItems);

    const categorizedResults = createCategorizedResults(grouped, currentCategories, maxResultsPerCategory, skipTopResults, contextCategory);

    return flattenCategorizedResults(categorizedResults);
}
